import { Component, OnInit } from '@angular/core';
import {
  PamlabApiServiceService,
  DataService,
  RouteService,
  ScreenSizeService,
} from '@/shared/services';
import { map, startWith, Subscription } from 'rxjs';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { FticCourseFilters } from '../../../../../server/types';
import { HttpErrorResponse } from '@angular/common/http';
import { environment } from '../../../../../environments/environment';
import { View } from '@/core/interfaces';
import { MatDialog } from '@angular/material/dialog';

@Component({
  selector: 'ftic-course-dwf-apr-form',
  templateUrl: './ftic-course-dwf-apr-form.component.html',
  styleUrl: './ftic-course-dwf-apr-form.component.css',
  standalone: false,
})
export class FticCourseDwfAprFormComponent implements OnInit {
  private subscription: Subscription = new Subscription();
  private initialPrograms: { name: string; college: string }[] = [];
  public view!: View;
  public fticCourseFilters: FticCourseFilters = {
    cohorts: [],
    courses: [],
    uwf_years: [],
    colleges: [],
    programs: [],
    terms: [],
  };
  public fticCourseForm = new FormGroup({
    cohort: new FormControl(2020, [Validators.required]),
    course: new FormControl('', [Validators.required]),
    hs_gpa_lower: new FormControl(0, [Validators.required]),
    hs_gpa_upper: new FormControl(5, [Validators.required]),
    college: new FormControl('Hal Marcus College of Science and Engineering', [
      Validators.required,
    ]),
    program: new FormControl('Cybersecurity', [Validators.required]),
    uwf_year: new FormControl('UWFYear1', [Validators.required]),
    terms: new FormControl<number[]>([202008]),
  });
  public filtersUpdating = false;
  public fieldsToDisable: string[] = [];
  public courseSelectHeight!: string;
  public filteredCourses: string[] = [];

  constructor(
    private _dialog: MatDialog,
    private _apiService: PamlabApiServiceService,
    private _dataService: DataService,
    private _routeService: RouteService,
    private screenSizeService: ScreenSizeService,
  ) {}

  ngOnInit(): void {
    this.subscription.add(
      this.screenSizeService.screenView$.subscribe((view) => {
        this.view = view;
      }),
    );

    // get first course data filter values
    this.filtersUpdating = true;
    const { cohort, college, program, uwf_year, terms } =
      this.fticCourseForm.value;
    this.fieldsToDisable = [
      'cohort',
      'course',
      'program',
      'uwf_year',
      'terms',
      'college',
    ];
    this._setFiltersActiveState(this.fieldsToDisable, false);
    this.subscription.add(
      this._apiService
        .getFticCourseDataFilters({
          cohort: cohort!,
          college: college!,
          program: program!,
          uwf_year: uwf_year!,
          term: terms![0],
        })
        .subscribe({
          next: (filters: FticCourseFilters) => {
            this._setFiltersActiveState(this.fieldsToDisable, true);
            this.fieldsToDisable = [];
            this.fticCourseFilters = filters;
            this.initialPrograms = filters.programs;
            this.fticCourseFilters.programs = this.initialPrograms
              .filter((prog) => prog.college === college)
              .sort((a, b) => a.name.localeCompare(b.name));
            this.fticCourseForm.patchValue({
              course: filters.courses[0],
              terms: [this.fticCourseFilters.terms[0].value],
            });
            this.filtersUpdating = false;

            this.subscription.add(
              this.fticCourseForm
                .get('course')!
                .valueChanges.pipe(
                  startWith(''),
                  map((value) => {
                    this.filteredCourses =
                      this.fticCourseFilters.courses.filter((course) =>
                        course.toLowerCase().includes(value!.toLowerCase()),
                      );

                    if (this.filteredCourses.length < 4) {
                      this.courseSelectHeight =
                        this.filteredCourses.length * 50 + 'px';
                    } else {
                      this.courseSelectHeight = '200px';
                    }
                  }),
                )
                .subscribe(),
            );
            this.view === 'web' && this.submitForm();
          },
          error: (error) => {
            if (error.status === 401) {
              this._routeService.navigate(environment['login_url']);
            }
          },
        }),
    );
  }

  ngOnDestroy(): void {
    setTimeout(() => {
      this.subscription.unsubscribe();
    }, 3000);
  }

  updateFilters(updatedField: 'cohort' | 'program' | 'uwf_year' | 'college') {
    const { cohort, college, program, uwf_year } = this.fticCourseForm.value;
    let term = Number(`${cohort!.toString()}08`);

    if (updatedField === 'uwf_year') {
      const year = Number(this.fticCourseForm.get('uwf_year')?.value?.at(-1)!);
      term = Number(`${cohort! + year - 1}08`);
    }

    this.fieldsToDisable = ['course', 'terms', 'uwf_year'];
    updatedField === 'cohort' && this.fieldsToDisable.push('program');
    updatedField === 'program' && this.fieldsToDisable.push('cohort');
    this._setFiltersActiveState(this.fieldsToDisable, false);

    this.filtersUpdating = true;
    this._apiService
      .getFticCourseDataFilters({
        cohort: cohort!,
        college: college!,
        program: program!,
        uwf_year: uwf_year!,
        term,
      })
      .subscribe({
        next: (filters) => {
          this._setFiltersActiveState(this.fieldsToDisable, true);
          this.fieldsToDisable = [];
          this.fticCourseFilters = filters;
          this.initialPrograms = filters.programs;
          this.fticCourseFilters.programs = this.initialPrograms
            .filter((prog) => prog.college === college)
            .sort((a, b) => a.name.localeCompare(b.name));
          this.fticCourseForm.patchValue({
            cohort: cohort!,
            course: filters.courses[0],
            terms: [this.fticCourseFilters.terms[0].value],
            uwf_year,
          });
          this.filteredCourses = this.fticCourseFilters.courses;
          this.filtersUpdating = false;
        },
        error: (error) => {
          console.log(error);
        },
      });
  }

  handleCollegeChange() {
    const { college } = this.fticCourseForm.value;
    this.fticCourseFilters.programs = this.initialPrograms
      .filter((prog) => prog.college === college)
      .sort((a, b) => a.name.localeCompare(b.name));
    this.fticCourseForm.patchValue({
      program: this.fticCourseFilters.programs[0].name,
    });
    this.updateFilters('program');
  }

  closeDialog() {
    this._dialog.closeAll();
  }

  submitForm() {
    this._dataService.setPendingData(true);

    const params: any = {
      ...this.fticCourseForm.value,
    };

    this.subscription.add(
      this._apiService.getFticCourseData(params).subscribe({
        next: (data) => {
          this._dataService.setData({ data });
          this._dataService.setPendingData(false);
        },
        error: (errorRes: HttpErrorResponse) => {
          if (errorRes.status === 401) {
            this._routeService.navigate(environment['login_url']);
          }
          this._dataService.setData({ error: errorRes.error });
          this._dataService.setPendingData(false);
        },
      }),
    );
  }

  _setFiltersActiveState(formKeys: string[], active: boolean) {
    formKeys.forEach((formKey) => {
      if (active) {
        this.fticCourseForm.get(formKey)?.enable();
      } else {
        this.fticCourseForm.get(formKey)?.disable();
      }
    });
  }
}
